import { FC } from 'react';
import s from './PaymentSummary.module.css';
import { Button } from 'components/common/Button';
import { Trans, useTranslation } from 'react-i18next';
import { Plan } from 'types/payment';
import { PaymentSelect } from '../PaymentSelect/PaymentSelect';

type PaymentSummaryProps = {
  plan: Plan;
  plans: Plan[];
  onPlanChange: (planId: string) => void;
  onPay?: () => void;
};

const currencySymbols: Record<string, string> = {
  AMD: '֏',
  EUR: '€',
  GBP: '£',
  RUB: '₽',
  USD: '$',
};

export const PaymentSummary: FC<PaymentSummaryProps> = ({
  plan,
  plans,
  onPlanChange,
  onPay,
}) => {
  const { t, i18n } = useTranslation();

  const price = getPrice(plan);

  const planOptions = plans.map((plan) => ({
    id: plan.planId,
    value: plan.name,
    label: getPrice(plan),
  }));

  return (
    <aside className={s.summary} aria-label='Выбранный тариф и стоимость'>
      <header className={`${s['summary__panel']} ${s['summary__header']}`}>
        <p className={s['summary__label']}>{t('payment.tariff')}</p>

        <PaymentSelect
          className={s['summary__select']}
          variant='tariff'
          value={plan.name}
          options={planOptions}
          onChange={onPlanChange}
          ariaLabel={t('payment.tariff')}
        />
      </header>

      <dl className={`${s['summary__panel']} ${s['summary__features']}`}>
        <div>
          <dt className={s['summary__label']}>{t('payment.tokens')}</dt>
          <dd className={s['summary__value']}>
            <Trans
              key={i18n.resolvedLanguage}
              t={t}
              i18nKey='payment.inMonth'
              components={[<>{plan.periodTokens}</>, <>{plan.weeklyTokens}</>]}
            />
          </dd>
        </div>

        {plan.features.map(({ id, name, value }) => (
          <div key={id}>
            <dt className={s['summary__label']}>{name}</dt>
            <dd className={s['summary__value']}>{value}</dd>
          </div>
        ))}
      </dl>

      <section className={s['summary__panel']} aria-label='Итого к оплате'>
        <dl className={s['summary__totals']}>
          <div className={s['summary__row']}>
            <dt className={s['summary__label']}>
              {t('payment.monthlyPayment')}
            </dt>
            <dd className={s['summary__amount']}>{price}</dd>
          </div>
          <div className={s['summary__row']}>
            <dt className={s['summary__label']}>{t('payment.taxes')}</dt>
            <dd className={s['summary__amount']}>0</dd>
          </div>
          <div className={s['summary__row']}>
            <dt className={s['summary__label']}>{t('payment.total')}</dt>
            <dd className={s['summary__amount']}>{price}</dd>
          </div>
        </dl>

        <Button
          className={s['summary__pay-btn']}
          onClick={onPay}
          isAccent
          isRounded
        >
          {t('payment.pay')}
        </Button>
        <p className={s['summary__renewal']}>{t('payment.updatedMonthly')}</p>
      </section>
    </aside>
  );
};

const getPrice = (plan: Plan) => {
  const primaryPrice = plan.prices.find((price) => price.currency !== 'USD');
  const usdPrice = plan.prices.find((price) => price.currency === 'USD');

  if (!primaryPrice || !usdPrice) {
    return `${+plan.priceMinor / 100}`;
  }

  const localPriceMinor = `${currencySymbols[primaryPrice.currency]}${+primaryPrice.priceMinor / 100}`;

  const usdPriceMinor = `${currencySymbols[usdPrice.currency]}${+usdPrice.priceMinor / 100}`;

  return `${localPriceMinor} / ${usdPriceMinor}`;
};
